/**
 * Phase 6 — Redesigned Badge
 *
 * Compact pill, used for status, labels, counts.
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-[6px] font-medium whitespace-nowrap leading-none",
  {
    variants: {
      variant: {
        neutral: "bg-[hsl(var(--rd-surface-3))] text-[hsl(var(--rd-text-muted))] border border-[hsl(var(--rd-border-soft))]",
        accent:  "bg-[hsl(var(--rd-accent-500)/0.14)] text-[hsl(var(--rd-accent-300))] border border-[hsl(var(--rd-accent-500)/0.25)]",
        success: "bg-[hsl(var(--rd-success)/0.14)] text-[hsl(var(--rd-success))] border border-[hsl(var(--rd-success)/0.25)]",
        warning: "bg-[hsl(var(--rd-warning)/0.14)] text-[hsl(var(--rd-warning))] border border-[hsl(var(--rd-warning)/0.25)]",
        error:   "bg-[hsl(var(--rd-error)/0.14)]   text-[hsl(var(--rd-error))]   border border-[hsl(var(--rd-error)/0.25)]",
        outline: "bg-transparent text-[hsl(var(--rd-text-muted))] border border-[hsl(var(--rd-border))]",
      },
      size: {
        sm: "h-[18px] px-1.5 text-[10px]",
        md: "h-[20px] px-2   text-[11px]",
        lg: "h-[24px] px-2.5 text-[12px]",
      },
    },
    defaultVariants: {
      variant: "neutral",
      size: "md",
    },
  }
);

export interface RdBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const RdBadge = React.forwardRef<HTMLSpanElement, RdBadgeProps>(
  ({ className, variant, size, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
);
RdBadge.displayName = "RdBadge";
